import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ViewChild,
  computed,
  effect,
  input,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { catchError, of, tap } from 'rxjs';
import {
  QaiCallInsightsCardComponent,
  QaiSegmentedBarGraphComponent,
} from '@qai/ui';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { ReportingDashboardService } from 'src/app/services/reporting-dashboard/reporting-dashboard.service';
import { withDemoModule } from 'src/app/utils/demo-mode/demo-mode-context.util';
import { AppConstants } from 'src/app/app-constants';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ISkillScoreStatsRequest } from 'src/app/interfaces/reportingV2/iskill-score-stats-request';
import { ISkillScoreStatsResponse } from 'src/app/interfaces/reportingV2/iskill-score-stats-response';
import { LocaleService } from 'src/app/services/locale.service';
import { SimulationDetailListModalComponent } from 'src/app/pages/reporting-dashboard/components/modals/simulation-detail-list-modal/simulation-detail-list-modal.component';

@Component({
  selector: 'app-skills-averages',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    TranslocoModule,
    QaiCallInsightsCardComponent,
    QaiSegmentedBarGraphComponent,
    SimulationDetailListModalComponent,
  ],
  templateUrl: './skills-averages.component.html',
  styleUrls: ['./skills-averages.component.scss'],
})
export class SkillsAveragesComponent {
  readonly moduleAssignmentUuid = input<string>('');
  readonly simulationUuid = input<string>('');
  readonly accountUuid = input<string>('');
  readonly teamUuids = input<string[]>([]);
  readonly noMetricsMessage = this.translocoService.translate('NO_METRICS_MESSAGE', {}, 'reportingV2');

  skillsData = signal<ISkillScoreStatsResponse[]>([]);

  readonly modalSubheading = computed(() =>
    this.translocoService.translate('MODAL_SUBHEADING', { count: this.skillsData().length }, 'reportingV2')
  );

  readonly objectiveFooterLabel = computed(() => {
    const count = this.skillsData().length;
    return count > 5
      ? this.translocoService.translate('VIEW_ALL_OBJECTIVES', { count }, 'reportingV2')
      : undefined;
  });

  @ViewChild('listModal') private listModalRef!: SimulationDetailListModalComponent;

  constructor(
    private readonly reportingDashboardService: ReportingDashboardService,
    private readonly cdr: ChangeDetectorRef,
    private readonly translocoService: TranslocoService,
    private readonly destroyRef: DestroyRef,
    private readonly localeService: LocaleService,
  ) {
    effect(() => {
      this.loadData();
    });
  }

  get heading(): string {
    return this.translocoService.translate('SKILLS', {}, 'reportingV2');
  }

  get description(): string {
    return this.translocoService.translate('SKILLS_SCORE_STATS_DESCRIPTION', {}, 'reportingV2');
  }

  get legend(): Array<{ label: string; kind: 'success' | 'info' | 'warning' }> {
    return [
      { label: this.translocoService.translate('EXPERT', {}, 'reportingV2'), kind: 'success' },
      { label: this.translocoService.translate('PROFICIENT', {}, 'reportingV2'), kind: 'info' },
      { label: this.translocoService.translate('DEVELOPING', {}, 'reportingV2'), kind: 'warning' }
    ];
  }

  private loadData(): void {
    const accountUuid = this.accountUuid();
    const moduleAssignmentUuid = this.moduleAssignmentUuid();
    const simulationUuid = this.simulationUuid();
    const teamUuids = this.teamUuids() ?? [];

    if (!accountUuid || !moduleAssignmentUuid || !simulationUuid) {
      this.skillsData.set([]);
      console.log(
        'Invalid accountUuid, moduleAssignmentUuid, or simulationUuid',
      );
      return;
    }

    const skillScoreStatsPayload: ISkillScoreStatsRequest = {
      moduleAssignmentUuid,
      simulationUuid,
      accountUuid,
      teamUuids,
    };

    this.reportingDashboardService
      .getSkillScoreStats(skillScoreStatsPayload, withDemoModule(AppConstants.NEW_REPORTING_UI))
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap((items) => {
          this.skillsData.set(items);
          this.cdr.markForCheck();
        }),
        catchError((err) => {
          console.error('SkillsAveragesComponent: load failed', err);
          this.skillsData.set([]);
          this.cdr.markForCheck();
          return of([]);
        }),
      )
      .subscribe();
  }

  readonly displayedSkillsData = computed(() => {
    const getDeveloping = (item: ISkillScoreStatsResponse) =>
      item.skillProgress.find(sp => sp.skillLevel === 'DEVELOPING')?.progressStatus.percentage ?? 0;
    const getProficient = (item: ISkillScoreStatsResponse) =>
      item.skillProgress.find(sp => sp.skillLevel === 'PROFICIENT')?.progressStatus.percentage ?? 0;
    return [...this.skillsData()]
      .sort((a, b) => {
        const devDiff = getDeveloping(b) - getDeveloping(a);
        if (devDiff !== 0) return devDiff;
        return getProficient(b) - getProficient(a);
      })
      .slice(0, 5);
  });

  onViewAllObjectives(): void {
    this.listModalRef?.open();
  }

  /**
   * Maps API response to segments format for qai-segmented-bar-graph
   * Order: EXPERT (success), PROFICIENT (info), DEVELOPING (warning)
   * Filters out segments with 0 percentage
   */
  mapToSegments(skillProgress: ISkillScoreStatsResponse['skillProgress']): Array<{ value: number; kind: 'success' | 'info' | 'warning' }> {
    // Find the percentage for each skill level
    const expertProgress = skillProgress.find(sp => sp.skillLevel === 'EXPERT');
    const proficientProgress = skillProgress.find(sp => sp.skillLevel === 'PROFICIENT');
    const developingProgress = skillProgress.find(sp => sp.skillLevel === 'DEVELOPING');

    const segments = [
      {
        value: parseFloat(this.localeService.formatScoreValue((expertProgress?.progressStatus.percentage ?? 0).toString(), 1)),
        kind: 'success' as const
      },
      {
        value: parseFloat(this.localeService.formatScoreValue((proficientProgress?.progressStatus.percentage ?? 0).toString(), 1)),
        kind: 'info' as const
      },
      {
        value: parseFloat(this.localeService.formatScoreValue((developingProgress?.progressStatus.percentage ?? 0).toString(), 1)),
        kind: 'warning' as const
      }
    ];

    // Filter out segments with 0 percentage
    return segments.filter(segment => segment.value > 0);
  }

  /**
   * Generates aria label for accessibility
   */
  generateAriaLabel(skillProgress: ISkillScoreStatsResponse['skillProgress']): string {
    const expertProgress = skillProgress.find(sp => sp.skillLevel === 'EXPERT');
    const proficientProgress = skillProgress.find(sp => sp.skillLevel === 'PROFICIENT');
    const developingProgress = skillProgress.find(sp => sp.skillLevel === 'DEVELOPING');

    const expert = expertProgress?.progressStatus.percentage ?? 0;
    const proficient = proficientProgress?.progressStatus.percentage ?? 0;
    const developing = developingProgress?.progressStatus.percentage ?? 0;

    return `${this.localeService.formatScoreValue(expert.toString(), 1)}% expert, ${this.localeService.formatScoreValue(proficient.toString(), 1)}% proficient, ${this.localeService.formatScoreValue(developing.toString(), 1)}% developing`;
  }
}
